import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable, tap, catchError, of, finalize } from 'rxjs';
import { IDataLayerTableRowViewModel } from 'src/app/models/viewModels/dataLayerTableRowViewModel';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { IDetailField, DetailFieldKey } from 'src/app/models/viewModels/detailField.interface';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { QueryParams } from 'src/app/models/navigation/navigationParams.interface';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { DataLayerHttpService } from 'src/app/services/httpServices/dataLayerHttpService/data-layer-http.service';
import { DataLayerTableRowViewModelHelper } from 'src/app/models/viewModels/dataLayerTableRowViewModel';
import { getDataSourceTypeIcon } from 'src/app/common/dataLayer/dataLayerUtils';
import { IStackedBarChartData } from 'src/app/components/dashboards/charts/stacked-bar-chart/stacked-bar-chart.component';
import { IPieChartDataItem } from 'src/app/components/dashboards/charts/pie-chart/pie-chart.component';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { formatFromDate } from 'src/app/common/datetime_utility';
import {
  DataLayerItemType,
  IDataItemAccessAnalytics,
  IDataItemTopAnalytics,
  IPlatformAccessByDate,
  ITopAgent,
  ITopUser,
} from 'src/app/models/api/dataLayer.model';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ensureUrlProtocol, truncateUrl } from 'src/app/common/stringUtils';
import {
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';

@Component({
  selector: 'app-data-lens-details',
  templateUrl: './data-layer-details.component.html',
  styleUrls: ['./data-layer-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataLayerDetailsComponent implements OnInit {
  public integration = input.required<string>();
  public dataItem = input.required<string>();
  public viewModel = input<IDataLayerTableRowViewModel>();

  public isLoading = signal<boolean>(false);
  public errorMessage = signal<string>('');
  public accessAnalytics = signal<IDataItemAccessAnalytics | null>(null);
  public topAnalytics = signal<IDataItemTopAnalytics | null>(null);
  public accessAnalyticsLoading = signal<boolean>(false);
  public topAnalyticsLoading = signal<boolean>(false);
  public contentFetched = signal<boolean>(false);
  public contentData = signal<string>('');
  public contentLoading = signal<boolean>(false);
  public startDate = signal<Date | undefined>(undefined);
  public endDate = signal<Date | undefined>(undefined);

  private readonly router = inject(Router);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly storeService = inject(StoreService);
  private readonly dataLayerHttpService = inject(DataLayerHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService);
  private readonly notifications = inject(NotificationsService);
  private readonly aiAgentStepsHttpService = inject(AiAgentStepsHttpService);

  public dataLayerItem$: Observable<IDataLayerTableRowViewModel | null>;
  public dataLayerItemSignal = toSignal(this.storeService.getSelectedDataLensItem$());

  public model: Signal<IDataLayerTableRowViewModel | undefined> = computed(() => {
    return this.dataLayerItemSignal() || this.viewModel();
  });

  public statusLabel: Signal<StatusLabelEnum> = computed(() => {
    const model = this.model();
    if (!model) return StatusLabelEnum.open;
    return model.statusLabel || StatusLabelEnum.open;
  });

  public canFetchContent: Signal<boolean> = computed(() => {
    const model = this.model();
    return !!model?.originalData?.fetchContentInfo;
  });

  public isWebsite: Signal<boolean> = computed(() => {
    const model = this.model();
    return model?.originalData?.dataType === DataLayerItemType.website;
  });

  public tabs: Signal<ISidepanelTab[]> = computed(() => [
    { labelPath: 'dataLensPage.detailsTabTitle' },
    { labelPath: 'dataLensPage.activityTabTitle' },
  ]);

  private readonly MAX_MONTH_OFFSET = 2;
  public monthOffset = signal(0);
  private preComputedDateRanges = signal<{ start: Date; end: Date }[]>([]);

  public canNavigatePrevious: Signal<boolean> = computed(() => {
    return this.accessAnalytics() !== null && this.monthOffset() < this.MAX_MONTH_OFFSET;
  });

  public canNavigateNext: Signal<boolean> = computed(() => {
    return this.accessAnalytics() !== null && this.monthOffset() > 0;
  });

  public detailsFields: Signal<IDetailField[]> = computed(() => {
    const model = this.model();
    if (!model) return [];

    const urlValue = model.originalData?.url || '';

    const fields: IDetailField[] = [
      {
        key: DetailFieldKey.Name,
        value: model.resourceName,
      },
      {
        key: DetailFieldKey.Type,
        value: model.dataType,
        iconPath: model.resourceTypeIcon,
      },
      {
        key: DetailFieldKey.AssociatedRisk,
        value: model.associatedRisk,
      },
      {
        key: DetailFieldKey.AccessCount,
        value: model.accessCount.toString(),
      },
      {
        key: DetailFieldKey.LastAccessed,
        value: formatFromDate(model.lastAccessed),
      },
      {
        key: DetailFieldKey.MSFTSensitivityLabel,
        value: model.sensitivityLabel || 'Not classified',
      },
      {
        key: DetailFieldKey.FileSnippet,
        value: this.getSnippetContent(),
        isSnippet: true,
      },
      {
        key: DetailFieldKey.URL,
        value: urlValue,
        isCopyable: true,
        copyableText: urlValue,
        isUrl: true,
      },
    ];

    return fields;
  });

  ngOnInit(): void {
    this.dataLayerItem$ = this.storeService.getSelectedDataLensItem$();
    this.isLoading.set(true);

    this.computeAllDateRanges(new Date());

    this.dataLayerHttpService
      .getDataLayerItem(this.dataItem())
      .pipe(
        tap((dataLayerItem) => {
          const viewModel = DataLayerTableRowViewModelHelper.createViewModel(dataLayerItem);
          this.storeService.setSelectedDataLensItem(viewModel);
        }),
        catchError((error) => {
          this.logger.error('Error loading data lens item', error);
          this.errorMessage.set('Failed to load data item details');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();

    // Load analytics data automatically for the new single-page design
    // Load both access and top analytics in parallel
    this.loadAccessAnalytics();
    this.loadTopAnalytics();
  }

  public viewDataItemActivity(): void {
    const dataItem = this.model();
    if (dataItem) {
      this.sidePanelService.close();
      const dataItemFilter = this._encodeDataItemFilter(dataItem.id, dataItem.resourceName);
      this.router.navigate(['/app/ai-activity'], {
        queryParams: {
          q: `dataItem:${dataItemFilter}`,
        },
      });
    }
  }

  public viewRisk(): void {
    const dataItem = this.model();
    if (dataItem) {
      this.sidePanelService.close();
      const dataItemFilter = this._encodeDataItemFilter(dataItem.id, dataItem.resourceName);
      this.router.navigate(['/app/ai-findings'], {
        queryParams: {
          q: `dataItem:${dataItemFilter}`,
          view: 'individuals',
        },
      });
    }
  }

  public viewAgentActivity(agentId: string | null | undefined): void {
    const dataItem = this.model();
    if (dataItem) {
      this.sidePanelService.close();
      const dataItemFilter = this._encodeDataItemFilter(dataItem.id, dataItem.resourceName);
      const query = agentId ? `dataItem:${dataItemFilter} agent:${agentId}` : `dataItem:${dataItemFilter}`;
      this.router.navigate(['/app/ai-activity'], { queryParams: { q: query } });
    }
  }

  public viewUserActivity(userId: string | null): void {
    const dataItem = this.model();
    if (dataItem) {
      this.sidePanelService.close();
      const dataItemFilter = this._encodeDataItemFilter(dataItem.id, dataItem.resourceName);
      const query = userId ? `dataItem:${dataItemFilter} actor:${userId}` : `dataItem:${dataItemFilter}`;
      this.router.navigate(['/app/ai-activity'], { queryParams: { q: query } });
    }
  }

  private _encodeDataItemFilter(id: string, name: string): string {
    return `${id}|${encodeURIComponent(name)}`;
  }

  public getSourceTypeIcon(): string {
    const model = this.model();
    if (!model?.originalData) return '';

    const sourceType = model.originalData.sourceType;
    if (!sourceType) return '';

    return getDataSourceTypeIcon(sourceType);
  }

  public getPlatformIcon(): string {
    const model = this.model();
    return model?.platformIcon || '';
  }

  public closePanel(): void {
    this.sidePanelService.closePanel([QueryParams.dataItem, QueryParams.integration]);
  }

  public activityHistogramData: Signal<IStackedBarChartData> = computed(() => {
    const analytics = this.accessAnalytics();
    if (!analytics) {
      return { labels: [], datasets: [] };
    }

    return this.transformToStackedBarChart(
      analytics.platformAccessByDate || [],
      analytics.dateRangeStart,
      analytics.dateRangeEnd
    );
  });

  public accessByAgentData: Signal<IPieChartDataItem[]> = computed(() => {
    const analytics = this.topAnalytics();
    if (!analytics) return [];

    return this.transformAgentsToPieChart(analytics.topAgents, analytics.otherAgentsCount);
  });

  public accessByUserData: Signal<{ user: string; userId: string | null; count: number; percentage: number }[]> =
    computed(() => {
      const analytics = this.topAnalytics();
      if (!analytics) return [];

      const users: { user: string; userId: string | null; count: number }[] = analytics.topUsers.map((u: ITopUser) => ({
        user: u.userDisplayName || u.userId,
        userId: u.userId,
        count: u.count,
      }));

      if (analytics.otherUsersCount > 0) {
        users.push({
          user: 'Other users',
          userId: null,
          count: analytics.otherUsersCount,
        });
      }

      const sumCount = users.reduce((sum, u) => sum + u.count, 0) || 1;

      return users.map((u) => ({
        ...u,
        percentage: (u.count / sumCount) * 100,
      }));
    });

  public loadAccessAnalytics(startDate?: Date, endDate?: Date): void {
    const dataItemId = this.dataItem();
    if (!dataItemId) {
      this.logger.warn('loadAccessAnalytics: No dataItemId available');
      return;
    }

    const { start, end } = this.getDateRangeForOffset(this.monthOffset());
    const effectiveStart = startDate || start;
    const effectiveEnd = endDate || end;

    this.logger.info(`loadAccessAnalytics: Loading for item ${dataItemId}`, {
      startDate: effectiveStart.toISOString(),
      endDate: effectiveEnd.toISOString(),
    });
    this.accessAnalyticsLoading.set(true);

    this.dataLayerHttpService
      .getDataItemAccessAnalytics(dataItemId, effectiveStart, effectiveEnd)
      .pipe(
        tap((analytics) => {
          this.logger.info('loadAccessAnalytics: Data received', { analytics });
          this.accessAnalytics.set(analytics);
          this.startDate.set(effectiveStart);
          this.endDate.set(effectiveEnd);
        }),
        catchError((error) => {
          this.logger.error('loadAccessAnalytics: Failed to load', { error });
          this.errorMessage.set('Failed to load activity data');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.logger.info('loadAccessAnalytics: Loading complete');
          this.accessAnalyticsLoading.set(false);
        })
      )
      .subscribe();
  }

  private loadTopAnalytics(): void {
    const dataItemId = this.dataItem();
    if (!dataItemId) {
      this.logger.warn('loadTopAnalytics: No dataItemId available');
      return;
    }

    this.logger.info(`loadTopAnalytics: Loading for item ${dataItemId}`);
    this.topAnalyticsLoading.set(true);

    this.dataLayerHttpService
      .getDataItemTopAnalytics(dataItemId)
      .pipe(
        tap((analytics) => {
          this.logger.info('loadTopAnalytics: Data received', { analytics });
          this.topAnalytics.set(analytics);
        }),
        catchError((error) => {
          this.logger.error('loadTopAnalytics: Failed to load', { error });
          // Don't set error message - allow access chart to still work
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.logger.info('loadTopAnalytics: Loading complete');
          this.topAnalyticsLoading.set(false);
        })
      )
      .subscribe();
  }

  private transformToStackedBarChart(
    data: IPlatformAccessByDate[],
    dateRangeStart: string,
    dateRangeEnd: string
  ): IStackedBarChartData {
    const dateMap = new Map<string, Map<string, number>>();
    const platforms = new Set<string>();

    data.forEach((item) => {
      // Normalize date to YYYY-MM-DD format
      const normalizedDate = this.normalizeDate(item.date);
      if (!dateMap.has(normalizedDate)) {
        dateMap.set(normalizedDate, new Map());
      }
      const platformMap = dateMap.get(normalizedDate);
      if (platformMap) {
        platformMap.set(item.service, item.count);
      }
      platforms.add(item.service);
    });

    // Generate all dates in the range
    const allDates = this.generateAllDatesInRange(dateRangeStart, dateRangeEnd);

    const datasets = Array.from(platforms).map((platform) => ({
      label: this.getPlatformDisplayName(platform),
      data: allDates.map((date) => dateMap.get(date)?.get(platform) || 0),
      backgroundColor: this.getPlatformColor(platform),
    }));

    return {
      labels: allDates.map((date) => this.formatDateLabel(date)),
      datasets,
    };
  }

  private normalizeDate(dateStr: string): string {
    // If already in YYYY-MM-DD format, return as-is to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Handle full ISO datetime formats by extracting the date portion
    const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      return isoMatch[1];
    }
    // Fallback: parse and extract using UTC to avoid timezone shifts
    const date = new Date(dateStr);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private generateAllDatesInRange(startDateStr: string, endDateStr: string): string[] {
    const allDates: string[] = [];

    // Normalize start and end dates using the same method as API data
    const startNormalized = this.normalizeDate(startDateStr);
    const endNormalized = this.normalizeDate(endDateStr);

    // Parse the normalized dates
    const [startYear, startMonth, startDay] = startNormalized.split('-').map(Number);
    const [endYear, endMonth, endDay] = endNormalized.split('-').map(Number);

    const currentDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      allDates.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  }

  private transformAgentsToPieChart(topAgents: ITopAgent[], otherCount: number): IPieChartDataItem[] {
    const items: IPieChartDataItem[] = topAgents.map((agent, index) => ({
      id: agent.agentId,
      label: agent.agentName,
      value: agent.count,
      color: this.getAgentColor(index),
    }));

    if (otherCount > 0) {
      items.push({
        id: null,
        label: 'Other',
        value: otherCount,
        color: '#D2C0F5',
      });
    }

    return items;
  }

  private getPlatformDisplayName(platform: string): string {
    const displayNameMap: Record<string, string> = {
      // New API snake_case format
      copilot_studio: 'Copilot Studio',
      chatgpt: 'ChatGPT',
      azure_ai_foundry: 'Azure AI Foundry',
      vertex_ai: 'Google Vertex AI',
      m365_copilot: 'M365 Copilot',
      // Legacy camelCase format (for backward compatibility)
      chatGptEnterprise: 'ChatGPT',
      azureAiFoundry: 'Azure AI Foundry',
      copilotStudio: 'Copilot Studio',
      m365Copilot: 'M365 Copilot',
      vertexAi: 'Google Vertex AI',
    };
    return displayNameMap[platform] || platform;
  }

  private getPlatformColor(platform: string): string {
    const colorMap: Record<string, string> = {
      // New API snake_case format
      copilot_studio: '#4659E3',
      chatgpt: '#ADDE4B',
      azure_ai_foundry: '#29C1B3',
      vertex_ai: '#25205A',
      m365_copilot: '#8C5CE6',
      // Legacy camelCase format (for backward compatibility)
      chatGptEnterprise: '#ADDE4B',
      azureAiFoundry: '#29C1B3',
      copilotStudio: '#4659E3',
      m365Copilot: '#8C5CE6',
      vertexAi: '#25205A',
    };
    return colorMap[platform] || '#D2C0F5';
  }

  private getAgentColor(index: number): string {
    const colors = ['#ADDE4B', '#29C1B3', '#4659E3', '#8C5CE6', '#25205A'];
    return colors[index % colors.length];
  }

  private formatDateLabel(dateStr: string): string {
    // Parse YYYY-MM-DD directly to avoid timezone issues
    const [, month, day] = dateStr.split('-').map(Number);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${day}`;
  }

  public getFileType(): string {
    const model = this.model();
    return model?.dataType || 'File';
  }

  public getFileFormat(): string {
    const model = this.model();
    if (!model?.originalData) return DataLayerItemType.unknown;

    const fileExtension = model.originalData.file_extension;
    if (fileExtension) {
      return fileExtension.toLowerCase().replace(/^\./, '');
    }

    return model.dataType || DataLayerItemType.unknown;
  }

  public getCriticalFindings(): number {
    const model = this.model();
    const findings = model?.originalData?.findings || [];
    return findings.length;
  }

  public getWarningFindings(): number {
    return 0;
  }

  public getUrl(item: IDataLayerTableRowViewModel): string {
    return ensureUrlProtocol(item.originalData?.url);
  }

  public getTruncatedUrl(item: IDataLayerTableRowViewModel, maxLength = 40): string {
    return truncateUrl(this.getUrl(item), maxLength);
  }

  public hasUrl(item: IDataLayerTableRowViewModel): boolean {
    return !!item.originalData?.url;
  }

  public hasFindings(item: IDataLayerTableRowViewModel): boolean {
    const severities = item.findingsBySeverity;
    if (!severities) return false;
    return Object.values(severities).some((count) => count > 0);
  }

  public copyUrl(item: IDataLayerTableRowViewModel): void {
    const url = this.getUrl(item);
    if (url) {
      navigator.clipboard.writeText(url).then(
        () => {
          this.notifications.success('URL copied to clipboard');
        },
        (error) => {
          this.logger.error('Failed to copy URL to clipboard', error);
          this.notifications.error('Failed to copy URL');
        }
      );
    }
  }

  public fetchContent(): void {
    const model = this.model();
    const fetchContentInfo = model?.originalData?.fetchContentInfo;

    if (!fetchContentInfo) {
      this.logger.warn('fetchContent: No fetchContentInfo available');
      return;
    }

    this.contentLoading.set(true);
    this.contentFetched.set(true);
    this.contentData.set('');

    this.aiAgentStepsHttpService
      .getAgentStepSensitiveData(
        fetchContentInfo.integrationId,
        fetchContentInfo.stepId,
        SensitiveDataRequestActions.snippet
      )
      .pipe(
        tap((response) => {
          if (response.singleSnippet) {
            this.contentData.set(response.singleSnippet);
          } else if (response.snippet) {
            const snippetValues = Object.values(response.snippet);
            if (snippetValues.length > 0) {
              this.contentData.set(snippetValues.join('\n\n'));
            } else {
              this.contentData.set('No content available');
            }
          } else {
            this.contentData.set('No content available');
          }
        }),
        catchError((error) => {
          this.logger.error('fetchContent: Failed to fetch content', error);
          this.notifications.error('Failed to fetch content');
          this.contentData.set('Failed to fetch content');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.contentLoading.set(false))
      )
      .subscribe();
  }

  public getSnippetContent(): string {
    return this.contentData() || 'Content snippet not yet available';
  }

  public getTotalAccesses(): number {
    const analytics = this.topAnalytics();
    if (!analytics) return 0;

    const agentsTotal = analytics.topAgents.reduce((sum, agent) => sum + agent.count, 0);
    return agentsTotal + analytics.otherAgentsCount;
  }

  public getDateRange(): string {
    const analytics = this.accessAnalytics();
    if (!analytics) return '';

    const startDate = new Date(analytics.dateRangeStart);
    const endDate = new Date(analytics.dateRangeEnd);

    const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startFormatted = startDate.toLocaleDateString('en-US', formatOptions);
    const endFormatted = endDate.toLocaleDateString('en-US', formatOptions);

    return `${startFormatted} - ${endFormatted}`;
  }

  public getSensitivityLabel(): string | null {
    const model = this.model();
    if (!model?.originalData) return null;

    return model.originalData.sensitivity_label || null;
  }

  public getSensitiveLocation(): string | null {
    const model = this.model();
    if (!model?.originalData) return null;

    const data = model.originalData as { sensitive_location?: string };
    return data.sensitive_location || null;
  }

  public getSensitiveInfo(): string | null {
    const model = this.model();
    if (!model?.originalData) return null;

    const data = model.originalData as { sensitive_info?: string };
    return data.sensitive_info || null;
  }

  public hasSensitivityIndicators(): boolean {
    return !!(this.getSensitivityLabel() || this.getSensitiveLocation() || this.getSensitiveInfo());
  }

  public navigateDateRange(direction: number): void {
    if (!this.accessAnalytics()) return;
    if (direction < 0 && !this.canNavigatePrevious()) return;
    if (direction > 0 && !this.canNavigateNext()) return;

    const newOffset = this.monthOffset() - direction;
    this.monthOffset.set(newOffset);

    const { start, end } = this.getDateRangeForOffset(newOffset);
    // Only reload access analytics, NOT top analytics (it's fixed 90-day data)
    this.loadAccessAnalytics(start, end);
  }

  private getDateRangeForOffset(offset: number): { start: Date; end: Date } {
    const ranges = this.preComputedDateRanges();
    if (ranges.length > 0 && offset >= 0 && offset <= this.MAX_MONTH_OFFSET) {
      return ranges[offset];
    }
    return this.computeDateRangeForOffset(offset, new Date());
  }

  private computeDateRangeForOffset(offset: number, referenceDate: Date): { start: Date; end: Date } {
    const referenceDay = referenceDate.getDate();
    const referenceYear = referenceDate.getFullYear();
    const referenceMonth = referenceDate.getMonth();

    const startMonth = referenceMonth - offset - 1;
    const endMonth = referenceMonth - offset;

    const startYear = referenceYear + Math.floor(startMonth / 12);
    const endYear = referenceYear + Math.floor(endMonth / 12);

    const normalizedStartMonth = ((startMonth % 12) + 12) % 12;
    const normalizedEndMonth = ((endMonth % 12) + 12) % 12;

    const startDay = this.getValidDayForMonth(startYear, normalizedStartMonth, referenceDay);
    const endDay = offset === 0 ? referenceDay : this.getValidDayForMonth(endYear, normalizedEndMonth, referenceDay);

    const start = new Date(startYear, normalizedStartMonth, startDay);
    const end = offset === 0 ? referenceDate : new Date(endYear, normalizedEndMonth, endDay);

    return { start, end };
  }

  private getValidDayForMonth(year: number, month: number, targetDay: number): number {
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    return Math.min(targetDay, lastDayOfMonth);
  }

  private computeAllDateRanges(referenceDate: Date): void {
    const ranges: { start: Date; end: Date }[] = [];
    for (let offset = 0; offset <= this.MAX_MONTH_OFFSET; offset++) {
      ranges.push(this.computeDateRangeForOffset(offset, referenceDate));
    }
    this.preComputedDateRanges.set(ranges);
  }
}
